import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useLocation, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays } from "lucide-react";
import RedButton from "../RedButton";
import { getSectors, getCountries, getCompanyNames, getCompanySize, getCompanyRevenue, getCompanyType } from "../../service/globalSearch";

const jobTitleOptions = [
  { value: "Software Engineer", label: "Software Engineer" },
  { value: "Frontend Developer", label: "Frontend Developer" },
  { value: "Backend Developer", label: "Backend Developer" },
  { value: "Full Stack Developer", label: "Full Stack Developer" },
  { value: "Product Manager", label: "Product Manager" },
  { value: "UI/UX Designer", label: "UI/UX Designer" },
  { value: "Data Analyst", label: "Data Analyst" },
  { value: "QA Engineer", label: "QA Engineer" },
];

const datePostedOptions = ["24 Hours", "7 Days", "28 Days", "Custom"];

export default function SurveyFilters({ activeTab }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isSurveyTab = activeTab === "survey";
  const isPeopleTab = activeTab === "people";
  const isCompaniesTab = activeTab === "companies";

  // Dynamic Options States
  const [dynamicSectors, setDynamicSectors] = useState([]);
  const [dynamicCountries, setDynamicCountries] = useState([]);
  const [dynamicCompanies, setDynamicCompanies] = useState([]);
  const [dynamicCompanySizes, setDynamicCompanySizes] = useState([]);
  const [dynamicCompanyTypes, setDynamicCompanyTypes] = useState([]);

  // Date Range State for Survey Custom Filter
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Local Form State
  const [formData, setFormData] = useState({
    // Company Tab Keys
    company_industry: [],
    company_location: [],
    company_size: [],
    company_type: [],

    // Survey Tab Keys
    survey_sector: [],
    survey_industry: [],
    survey_country: [],
    date_posted: "",

    // People Tab Keys
    people_industry: [],
    people_job_title: "",
    people_company: [],
    people_country: [],
  });

  // Fetch dynamic filter options on mount
  useEffect(() => {
    const loadDynamicOptions = async () => {
      try {
        const [sectorsRes, countriesRes, companiesRes, sizesRes, typesRes] = await Promise.all([
          getSectors(),
          getCountries(),
          getCompanyNames(),
          getCompanySize(),
          getCompanyType(),
        ]);

        if (sectorsRes.status === 200) {
          setDynamicSectors(sectorsRes.data?.data || []);
        }
        if (countriesRes.status === 200) {
          setDynamicCountries(countriesRes.data?.data || []);
        }
        if (companiesRes.status === 200) {
          const raw = companiesRes.data?.data || [];
          setDynamicCompanies(raw.flat());
        }
        if (sizesRes.status === 200) {
          setDynamicCompanySizes(sizesRes.data?.data || []);
        }
        if (typesRes.status === 200) {
          setDynamicCompanyTypes(typesRes.data?.data || []);
        }
      } catch (err) {
        console.error("Error loading dynamic filter options:", err);
      }
    };

    loadDynamicOptions();
  }, []);

  // Options lists mapping
  const sectorOptions = dynamicSectors.length > 0
    ? dynamicSectors.map((item) => ({ value: String(item.question_answer_id), label: item.answer }))
    : [
      { value: "Technology", label: "Technology" },
      { value: "Healthcare", label: "Healthcare" },
      { value: "Finance", label: "Finance" },
      { value: "Education", label: "Education" },
    ];

  const countryOptions = dynamicCountries.length > 0
    ? dynamicCountries.map((item) => ({ value: String(item.id), label: item.country_name }))
    : [
      { value: "India", label: "India" },
      { value: "United Kingdom", label: "United Kingdom" },
      { value: "United States", label: "United States" },
      { value: "Canada", label: "Canada" },
    ];

  const companyOptions = dynamicCompanies.length > 0
    ? dynamicCompanies.map((item) => ({ value: item.company_name, label: item.company_name }))
    : [
      { value: "Google", label: "Google" },
      { value: "Microsoft", label: "Microsoft" },
      { value: "Amazon", label: "Amazon" },
      { value: "Meta", label: "Meta" },
    ];

  const companySizeOptions = dynamicCompanySizes.length > 0
    ? dynamicCompanySizes.map((item) => ({ value: String(item.question_answer_id), label: item.answer }))
    : [
      { value: "51-200", label: "51-200" },
      { value: "201-1000", label: "201-1000" },
      { value: "1001-5000", label: "1001-5000" },
      { value: "1000+", label: "1000+" },
    ];

  const companyTypeOptions = dynamicCompanyTypes.length > 0
    ? dynamicCompanyTypes.map((item) => ({ value: String(item.question_answer_id), label: item.answer }))
    : [
      { value: "Startup", label: "Startup" },
      { value: "SMB", label: "SMB" },
      { value: "Enterprise", label: "Enterprise" },
      { value: "Private", label: "Private" },
      { value: "Public", label: "Public" },
    ];

  // Sync state with URL Search Parameters
  useEffect(() => {
    const parseMultiSelect = (paramKey, optionsList) => {
      const val = searchParams.get(paramKey);
      if (!val) return [];
      const ids = val.split(",");
      return ids.map((id) => {
        const matched = optionsList.find((opt) => String(opt.value) === String(id));
        return matched ? matched : { value: id, label: id };
      });
    };

    const parseCheckbox = (paramKey) => {
      const val = searchParams.get(paramKey);
      return val ? val.split(",") : [];
    };

    const datePostedVal = searchParams.get("date_posted") || "";
    const startDateVal = searchParams.get("start_date") ? new Date(searchParams.get("start_date")) : null;
    const endDateVal = searchParams.get("end_date") ? new Date(searchParams.get("end_date")) : null;

    setFormData({
      company_industry: parseMultiSelect("company_industry", sectorOptions),
      company_location: parseMultiSelect("company_location", countryOptions),
      company_size: parseCheckbox("company_size"),
      company_type: parseCheckbox("company_type"),

      survey_sector: parseMultiSelect("survey_sector", sectorOptions),
      survey_industry: parseMultiSelect("survey_industry", sectorOptions),
      survey_country: parseMultiSelect("survey_country", countryOptions),
      date_posted:
        startDateVal && endDateVal
          ? "Custom"
          : datePostedVal === "today"
            ? "24 Hours"
            : datePostedVal === "last_7_days"
              ? "7 Days"
              : datePostedVal === "last_30_days"
                ? "28 Days"
                : datePostedVal === "custom"
                  ? "Custom"
                  : datePostedVal
                    ? datePostedVal.charAt(0).toUpperCase() + datePostedVal.slice(1)
                    : "",

      people_industry: parseMultiSelect("people_industry", sectorOptions),
      people_job_title: searchParams.get("people_job_title") || "",
      people_company: parseMultiSelect("people_company", companyOptions),
      people_country: parseMultiSelect("people_country", countryOptions),
    });

    if (startDateVal && endDateVal) {
      setDateRange([startDateVal, endDateVal]);
    } else {
      setDateRange([null, null]);
    }
  }, [searchParams, dynamicSectors, dynamicCountries, dynamicCompanies, dynamicCompanySizes, dynamicCompanyTypes]);

  const handleSelectChange = (name, selected) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected || [],
    }));
  };

  const handleMultiCheckbox = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter((item) => item !== value)
        : [...prev[name], value],
    }));
  };

  const handleReset = () => {
    setFormData({
      company_industry: [],
      company_location: [],
      company_size: [],
      company_type: [],

      survey_sector: [],
      survey_industry: [],
      survey_country: [],
      date_posted: "",

      people_industry: [],
      people_job_title: "",
      people_company: [],
      people_country: [],
    });
    setDateRange([null, null]);

    const params = new URLSearchParams();
    const term = searchParams.get("searchTerm");
    if (term) {
      params.set("searchTerm", term);
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleApplyFilters = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const params = new URLSearchParams(window.location.search);

    // Reset filters of other tabs from search parameters to avoid dirty states
    const clearParams = (keys) => {
      keys.forEach((k) => params.delete(k));
    };

    if (isCompaniesTab) {
      clearParams([
        "survey_sector",
        "survey_industry",
        "survey_country",
        "date_posted",
        "start_date",
        "end_date",
        "people_industry",
        "people_job_title",
        "people_company",
        "people_country",
      ]);

      if (formData.company_industry && formData.company_industry.length > 0) {
        params.set("company_industry", formData.company_industry.map((opt) => opt.value).join(","));
      } else {
        params.delete("company_industry");
      }

      if (formData.company_location && formData.company_location.length > 0) {
        params.set("company_location", formData.company_location.map((opt) => opt.value).join(","));
      } else {
        params.delete("company_location");
      }

      if (formData.company_size && formData.company_size.length > 0) {
        params.set("company_size", formData.company_size.join(","));
      } else {
        params.delete("company_size");
      }

      if (formData.company_type && formData.company_type.length > 0) {
        params.set("company_type", formData.company_type.join(","));
      } else {
        params.delete("company_type");
      }
    }

    if (isSurveyTab) {
      clearParams([
        "company_industry",
        "company_location",
        "company_size",
        "company_type",
        "people_industry",
        "people_job_title",
        "people_company",
        "people_country",
      ]);

      if (formData.survey_sector && formData.survey_sector.length > 0) {
        params.set("survey_sector", formData.survey_sector.map((opt) => opt.value).join(","));
      } else {
        params.delete("survey_sector");
      }



      if (formData.survey_country && formData.survey_country.length > 0) {
        params.set("survey_country", formData.survey_country.map((opt) => opt.value).join(","));
      } else {
        params.delete("survey_country");
      }

      if (formData.date_posted) {
        if (formData.date_posted === "Custom" && startDate && endDate) {
          const formatD = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };
          params.set("start_date", formatD(startDate));
          params.set("end_date", formatD(endDate));
          params.delete("date_posted");
        } else {
          const val =
            formData.date_posted === "24 Hours"
              ? "today"
              : formData.date_posted === "7 Days"
                ? "last_7_days"
                : formData.date_posted === "28 Days"
                  ? "last_30_days"
                  : formData.date_posted.toLowerCase();
          params.set("date_posted", val);
          params.delete("start_date");
          params.delete("end_date");
        }
      } else {
        params.delete("date_posted");
        params.delete("start_date");
        params.delete("end_date");
      }
    }

    if (isPeopleTab) {
      clearParams([
        "company_industry",
        "company_location",
        "company_size",
        "company_type",
        "survey_sector",
        "survey_industry",
        "survey_country",
        "date_posted",
        "start_date",
        "end_date",
      ]);

      if (formData.people_industry && formData.people_industry.length > 0) {
        params.set("people_industry", formData.people_industry.map((opt) => opt.value).join(","));
      } else {
        params.delete("people_industry");
      }

      if (formData.people_job_title && formData.people_job_title.trim()) {
        params.set("people_job_title", formData.people_job_title.trim());
      } else {
        params.delete("people_job_title");
      }

      if (formData.people_company && formData.people_company.length > 0) {
        params.set("people_company", formData.people_company.map((opt) => opt.value).join(","));
      } else {
        params.delete("people_company");
      }

      if (formData.people_country && formData.people_country.length > 0) {
        params.set("people_country", formData.people_country.map((opt) => opt.value).join(","));
      } else {
        params.delete("people_country");
      }
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  return (
    <div className="surveyfilterbox">
      <div className="bg-white border rounded-4 overflow-hidden">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center p-3 py-4 border-bottom">
          <h6 className="fw-bold mb-0 ft-14" style={{ color: "#18212F" }}>Filters</h6>

          <button
            className="reset-btn ft-12 fw-bold text-decoration-none p-0 fw-semibold"
            type="button"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>

        {/* --- COMPANIES TAB FILTERS --- */}
        {isCompaniesTab && (
          <>
            {/* Sector */}
            <div className="p-4 border-bottom pb-4">
              <label className="fw-bold ft-13 inter mb-2 d-block">Sector</label>
              <Select
                isMulti
                options={sectorOptions}
                value={formData.company_industry}
                onChange={(selected) => handleSelectChange("company_industry", selected)}
                placeholder="Select Sector"
                closeMenuOnSelect={false}
                className="custom-react-select"
                classNamePrefix="selectcustome"
              />
            </div>

            {/* Country */}
            <div className="p-4 border-bottom pb-4">
              <label className="fw-bold ft-13 inter mb-2 d-block">Country</label>
              <Select
                isMulti
                options={countryOptions}
                value={formData.company_location}
                onChange={(selected) => handleSelectChange("company_location", selected)}
                placeholder="Select Country"
                closeMenuOnSelect={false}
                className="custom-react-select"
                classNamePrefix="selectcustome"
              />
            </div>

            {/* Company Size */}
            <div className="p-4 border-bottom pb-4">
              <h6 className="fw-bold ft-13 inter mb-2 d-block">Company Size</h6>
              <div className="d-flex flex-column gap-2">
                {companySizeOptions.map((item) => (
                  <div className="form-check" key={item.value}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`size-${item.value}`}
                      checked={formData.company_size.includes(item.value)}
                      onChange={() => handleMultiCheckbox("company_size", item.value)}
                    />
                    <label className="form-check-label ms-2" htmlFor={`size-${item.value}`}>
                      {item.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Company Type */}
            <div className="p-4 border-bottom pb-4">
              <h6 className="fw-bold ft-13 inter mb-2 d-block">Company Type</h6>
              <div className="d-flex flex-column gap-2">
                {companyTypeOptions.map((item) => (
                  <div className="form-check" key={item.value}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`type-${item.value}`}
                      checked={formData.company_type.includes(item.value)}
                      onChange={() => handleMultiCheckbox("company_type", item.value)}
                    />
                    <label className="form-check-label ms-2" htmlFor={`type-${item.value}`}>
                      {item.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
       




            {/* --- SURVEY TAB FILTERS --- */}
            {isSurveyTab && (
              <>
                {/* Sector */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Sector</label>
                  <Select
                    isMulti
                    options={sectorOptions}
                    value={formData.survey_sector}
                    onChange={(selected) => handleSelectChange("survey_sector", selected)}
                    placeholder="Select Sector"
                    closeMenuOnSelect={false}
                    className="custom-react-select"
                  />
                </div>



                {/* Country */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Country</label>
                  <Select
                    isMulti
                    options={countryOptions}
                    value={formData.survey_country}
                    onChange={(selected) => handleSelectChange("survey_country", selected)}
                    placeholder="Select Country"
                    closeMenuOnSelect={false}
                    className="custom-react-select"
                  />
                </div>

                {/* Date Posted */}
                <div className="p-4 border-bottom pb-4">
                  <h6 className="fw-bold ft-13 inter mb-2 d-block">Date Posted</h6>
                  <div className="d-flex flex-column gap-3">
                    {datePostedOptions.map((item) => (
                      <div className="form-check" key={item}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="date_posted"
                          value={item}
                          checked={formData.date_posted === item}
                          onChange={(e) => setFormData((prev) => ({ ...prev, date_posted: e.target.value }))}
                        />
                        <label className="form-check-label ms-2">{item}</label>
                      </div>
                    ))}
                  </div>

                  {/* Nested Custom Date Range Box */}
                  {formData.date_posted === "Custom" && (
                    <div className="mt-3 p-4 rounded-3" style={{ backgroundColor: "#f4f7fa", border: "1px solid #eef2f6" }}>
                      <label className="fw-bold ft-12 inter mb-2 d-block" style={{ color: "#18212F" }}>
                        Custom Date Range
                      </label>
                      <div className="position-relative bg-white rounded-3 border border-light" style={{ height: "40px" }}>
                        <DatePicker
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={(update) => {
                            setDateRange(update);
                          }}
                          placeholderText="Select Date Range"
                          dateFormat="dd MMM yyyy"
                          className="custom-date-input border-0 w-100 h-100"
                        />
                        <CalendarDays
                          size={18}
                          className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"
                          style={{ pointerEvents: "none" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* --- PEOPLE TAB FILTERS --- */}
            {isPeopleTab && (
              <>
                {/* Industry */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Sector</label>
                  <Select
                    isMulti
                    options={sectorOptions}
                    value={formData.people_industry}
                    onChange={(selected) => handleSelectChange("people_industry", selected)}
                    placeholder="Select Sector"
                    closeMenuOnSelect={false}
                    className="custom-react-select"
                  />
                </div>

                {/* Job Title */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Job Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.people_job_title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, people_job_title: e.target.value }))}
                    placeholder="e.g. Backend Developer"
                    style={{
                      height: "38px",
                      fontSize: "13px",
                      border: "1px solid #cccccc",
                      borderRadius: "4px",
                      padding: "6px 12px"
                    }}
                  />
                </div>

                {/* Company */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Company</label>
                  <Select
                    isMulti
                    options={companyOptions}
                    value={formData.people_company}
                    onChange={(selected) => handleSelectChange("people_company", selected)}
                    placeholder="Select Company"
                    closeMenuOnSelect={false}
                    className="custom-react-select"
                  />
                </div>

                {/* Country */}
                <div className="p-3 border-bottom pb-4">
                  <label className="fw-bold ft-13 inter mb-2 d-block">Country</label>
                  <Select
                    isMulti
                    options={countryOptions}
                    value={formData.people_country}
                    onChange={(selected) => handleSelectChange("people_country", selected)}
                    placeholder="Select Country"
                    closeMenuOnSelect={false}
                    className="custom-react-select"
                  />
                </div>
              </>
            )}

            {/* Buttons */}
            <div className="p-4">
              <RedButton
                btntext="Apply Filters"
                name="applyFilters"
                onClick={handleApplyFilters}
                className="w-100 mb-3"
              />
              <button className="redhoverbtn w-100 ft-14 fw-bold text-black px-3 py-3 bgwhite rounded-2 cursor-target text-center border border-1 tw-transition-all tw-duration-300 tw-ease-in-out hover:tw-scale-105">
                Save Search
              </button>
            </div>
      </div>
    </div>
  );
}